/**
 * Single source of truth for "will this timeline entry actually render?".
 *
 * Several item/event components return `null` at render time (internal-access
 * properties or associations, system-seeded stage creations, incomplete
 * association-change pairs). `TimelineSection` truncates the list by raw item
 * count *before* render, so without a shared notion of renderability the slice
 * can hand the first N slots to items that all render nothing — leaving an
 * empty timeline with only an "Expand" button.
 *
 * The components delegate their null checks to the helpers here, and
 * `TimelineSection` filters with `isRenderableTimelineItem` before slicing, so
 * the count and the render agree. The components keep their own `null` returns
 * as the fail-closed security boundary — this module is about slot accounting,
 * not access control.
 */

import type { AssociationType } from '@/lib/crm/core/SchemaClient';

import { getBookingTimelineLabel } from './booking-labels';
import type { TimelineItem } from './collapseAssociationEvents';
import { ITEM_TYPES } from './collapseAssociationEvents';
import type {
  AssociationPayload,
  PropertyUpdatePayload,
  TimelineEvent,
  ViewStateLookups,
} from './types';
import { EVENT_TYPES } from './types';

/** Stage object types hidden from the timeline when created by seeding. */
export const STAGE_OBJECT_TYPES = [
  'ahoy_deal_pipeline_stage',
  'ahoy_contact_lifecycle_stage',
];

/**
 * An `object_created` event for a stage entity produced by seeding — system
 * noise, not user-relevant, so the timeline hides it.
 */
export function isStageSeedingCreate(event: TimelineEvent): boolean {
  return (
    STAGE_OBJECT_TYPES.includes(event.entity_type_name || '') &&
    event.source_id === 'stage_seeding'
  );
}

/**
 * An association is visible iff its type exists in lookups and is not marked
 * `internal`. Fail-closed: unknown types (missing from lookups) are hidden.
 */
export function isAssociationTypeVisible(
  associationType: AssociationType | undefined
): boolean {
  return !!associationType && associationType.value_read_access !== 'internal';
}

/** Pull the association_type_name off an association event's payload. */
function associationTypeNameOf(event: TimelineEvent): string {
  const payload = event.payload as AssociationPayload | undefined;
  return payload?.association_type_name ?? '';
}

/**
 * Merge property updates across `events` (last-wins) and return only the
 * entries whose schema grants `value_read_access: 'all'`, excluding
 * system-derived properties. A property is dropped when: it has no schema
 * (fail-closed), its read access is not `all`, or its schema is derived
 * (non-null `rollup_config` or `formula`) — matching what the PropertyUpdated
 * item/event components display. Derived rollups still show on the record —
 * the property view reads values, not timeline events — only their timeline
 * entries are hidden, since a rollup can fire on every activity touching the
 * entity and would bury real activity under a stream of self-referential
 * update noise.
 *
 * Cascade events absorbed into a property-update item are same-entity (the
 * cascade collapse stops at entity boundaries), so a single entity_type_name
 * (from the first event) keys the schema lookup for every property.
 */
export function collectVisibleUpdatedProperties(
  events: TimelineEvent[],
  lookups: ViewStateLookups
): Array<[string, unknown]> {
  const entityTypeName = events[0]?.entity_type_name;
  const merged: Record<string, unknown> = {};
  for (const event of events) {
    const payload = event.payload as PropertyUpdatePayload | undefined;
    Object.assign(merged, payload?.properties ?? {});
  }
  return Object.entries(merged).filter(([propName]) => {
    const schema = lookups.property_types[`${entityTypeName}.${propName}`];
    if (!schema) {
      return false;
    }
    if (schema.rollup_config != null || schema.formula != null) {
      return false;
    }
    return schema.value_read_access === 'all';
  });
}

/**
 * Whether a `single_event` item produces visible output, dispatched by the
 * event's type. Most event types always render; only property updates (all
 * fields internal, and not a booking event), stage-seeded creations, and
 * internal/unknown associations render nothing.
 */
function isRenderableSingleEvent(
  event: TimelineEvent,
  lookups: ViewStateLookups
): boolean {
  switch (event.event_type_name) {
    case EVENT_TYPES.PROPERTY_UPDATED:
      return (
        getBookingTimelineLabel(event) !== null ||
        collectVisibleUpdatedProperties([event], lookups).length > 0
      );
    case EVENT_TYPES.OBJECT_CREATED:
      return !isStageSeedingCreate(event);
    case EVENT_TYPES.ASSOCIATION_ADDED:
    case EVENT_TYPES.ASSOCIATION_REMOVED:
      return isAssociationTypeVisible(
        lookups.association_types[associationTypeNameOf(event)]
      );
    default:
      return true;
  }
}

/**
 * Whether a timeline item will render visible content given the current
 * lookups. Mirrors the `return null` conditions in the item/event components
 * (which delegate to the same helpers), so it can be used to filter the list
 * before truncating without the count drifting from what actually renders.
 */
export function isRenderableTimelineItem(
  item: TimelineItem,
  lookups: ViewStateLookups
): boolean {
  if (item.events.length === 0) {
    return false;
  }
  switch (item.type) {
    case ITEM_TYPES.PROPERTY_UPDATED:
      return collectVisibleUpdatedProperties(item.events, lookups).length > 0;
    case ITEM_TYPES.OBJECT_CREATED:
      return !isStageSeedingCreate(item.events[0]);
    case ITEM_TYPES.ASSOCIATION_ADDED:
    case ITEM_TYPES.ASSOCIATION_REMOVED:
      return isAssociationTypeVisible(
        lookups.association_types[associationTypeNameOf(item.events[0])]
      );
    case ITEM_TYPES.ASSOCIATION_CHANGED: {
      const added = item.events.find(
        e => e.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED
      );
      const removed = item.events.find(
        e => e.event_type_name === EVENT_TYPES.ASSOCIATION_REMOVED
      );
      if (!added || !removed) {
        return false;
      }
      return isAssociationTypeVisible(
        lookups.association_types[associationTypeNameOf(added)]
      );
    }
    case ITEM_TYPES.SINGLE_EVENT:
      return isRenderableSingleEvent(item.events[0], lookups);
    default:
      return true;
  }
}
